import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import Link from "next/link";

import styles from "./PublicProfileTabs.module.css";
import { PublicProfile } from "@dimensional-engineering/dimensional-models";
import { usePublicProfileContext } from "@/context/PublicProfileContext";
import SmallProfileImage from "@/components/shared/SmallProfileImage/SmallProfileImage";
import { Mixpanel } from "@/helpers/mixpanel";

type Props = {
  tabs: string[];
  publicProfile: PublicProfile | null;
  imageUrl: string;
};

export default function PublicProfileTabs(props: Props) {
  const [activeTab, setActiveTab] = useState<number>(0);
  const router = useRouter();
  const profileHeadeRref = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const { setModal } = usePublicProfileContext();

  useEffect(() => {
    if (router.asPath.includes("#")) {
      const index = router.asPath.indexOf("#");
      const found = props.tabs.indexOf(router.asPath.slice(index + 1));
      if (found > 0) {
        setActiveTab(found);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath]);

  useEffect(() => {
    if (mainRef.current && profileHeadeRref.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.intersectionRatio < 1 && profileHeadeRref.current) {
            profileHeadeRref.current.style.display = "flex";
          } else {
            if (profileHeadeRref.current) {
              profileHeadeRref.current.style.display = "none";
            }
          }
        },
        { threshold: [1] }
      );

      observer.observe(mainRef.current);

      return () => {
        if (mainRef.current) {
          // eslint-disable-next-line react-hooks/exhaustive-deps
          observer.unobserve(mainRef.current);
        }
      };
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = props.tabs.map((t) => document.getElementById(t));

      sections.forEach((section, index) => {
        if (section && window.scrollY >= section.offsetTop - 150) {
          setActiveTab(index);
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [props.tabs]);

  const mapped = props.tabs.map((t, i) => {
    return (
      <Link
        onClick={(e) => {
          e.preventDefault();
          setActiveTab(i);

          const target = document.getElementById(t);
          if (target) {
            const headerOffset = 110;
            const elementPosition = target.getBoundingClientRect().top;
            const offsetPosition =
              elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
              top: offsetPosition,
            });
          }

          window.history.pushState(null, "", `#${t}`);
        }}
        className={activeTab === i ? styles.tabActive : styles.tab}
        href={`#${t}`}
        key={t}
        scroll={false}
      >
        {t.toUpperCase()}
      </Link>
    );
  });

  return (
    <div ref={mainRef} className={styles.main}>
      <div ref={profileHeadeRref} id="header" className={styles.profileHeader}>
        <SmallProfileImage
          publicProfile={props.publicProfile}
          imageUrl={props.imageUrl}
        />
        <div className={styles.profileNamesDiv}>
          {props.publicProfile?.name && (
            <h3 className={styles.name}>{props.publicProfile.name}</h3>
          )}
          <h4 className={styles.username}>@{props.publicProfile?.userName}</h4>
        </div>
        <button
          onClick={() => {
            Mixpanel?.track(
              "View full profile CTA clicked",
              {
                profile_username: props.publicProfile?.userName,
              },
              "d"
            );
            setModal(true);
          }}
          className={styles.button}
        >
          Connect
        </button>
      </div>
      <div className={styles.wrapper}>{mapped}</div>
    </div>
  );
}
